import * as yup from 'yup';
import { Smartphone } from 'react-feather';
import { useEffect } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { gql } from '__generated__';

import { getServerErrors } from './helpers';
import UI from './UI';
import config from '../config';
import useMutation from '../api/useMutation';
import useValidation from './useValidation';

export const VerifyAuthenticatorCodeMutation = gql(`
  mutation VerifyAuthenticatorCode($input: VerifyAuthenticatorCodeInput!) {
    verifyAuthenticatorCode(input: $input) {
      id
    }
  }
`);

const authenticatorSchema = yup.object().shape({
  code: yup.string().required(),
});

interface UseAuthenticatorVerificationModalProps {
  onSuccess: () => void;
  onCancel?: () => void;
}

const useAuthenticatorVerificationModal = ({
  onSuccess, onCancel,
}: UseAuthenticatorVerificationModalProps) => {
  const { t } = useTranslation('common');

  const getValues = () => ({
    code: '',
  });

  const form = useForm({
    values: getValues(),
  });

  const [verify, { data, error, loading, reset }] = useMutation(VerifyAuthenticatorCodeMutation, {
    variables: {
      input: {
        code: form.values.code,
      },
    },
    onCompleted: () => {
      onSuccess();
      closeModal();
    },
  });

  const { errors } = useValidation({
    schema: authenticatorSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  useEffect(() => {
    if (form.values.code.trim().length === config.authenticatorCodeLength) {
      verify();
    }
  }, [form.values.code, verify]);

  const [openModal, closeModal] = useModal(() => (
    <UI.Modal close={closeModal}>
      <UI.FormGrid>
        <UI.H4>
          <UI.Icon>
            <Smartphone />
          </UI.Icon>
          {' '}
          {t('authenticator.verification_required')}
        </UI.H4>

        <UI.Div>
          {t('authenticator.verification_required_description')}
        </UI.Div>

        <UI.InputGroup sc={{ invalid: error && !!errors.code }}>
          <UI.InputLabel htmlFor="Pin_0">
            {t('authenticator.authenticator_code')}
          </UI.InputLabel>
          <UI.PinCodeInput
            value={form.values.code}
            onChange={(value) => form.set('code', value)}
            length={config.authenticatorCodeLength}
            name="code"
            spacingAfter={[2]}
            placeholder="123456"
            inputMode="numeric"
            id="Pin"
            autoFocus
            sc={{ size: 'lg' }}
          />
          <UI.ErrorMessages attribute={t('authenticator.authenticator_code')} errors={errors.code} />
        </UI.InputGroup>

        <UI.HR />

        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            onClick={() => { onCancel?.(); closeModal(); }}
            disabled={loading || !!data}
          >
            {t('cancel')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Modal>
  ), [form, errors, loading, data, error, form.values.code]);

  const handleOpen = () => {
    // Reset all state on open.
    reset();
    form.reset(getValues());

    openModal();
  };

  return [handleOpen, closeModal];
};

export default useAuthenticatorVerificationModal;
